import { withDeltas, type RunRecord } from "../runHistory";

/**
 * Session run history with deltas vs the previous run of the same
 * config (config_hash equality). Newest first. In-memory only —
 * refresh clears it.
 */
export function RunHistoryPanel({ history }: { history: RunRecord[] }) {
  if (history.length === 0) return null;
  const entries = withDeltas(history);
  const multiConfig = new Set(history.map((r) => r.configHash)).size > 1;
  return (
    <section className="border border-paper-border p-4 dark:border-term-border">
      <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
        Run history
      </h2>
      <p className="mb-2 text-[10px] text-paper-hint dark:text-term-hint">
        last {history.length} runs · Δ vs previous run of same config · session
        only
      </p>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-paper-label dark:text-term-label">
              <th className="py-1 pr-2 font-semibold">Time</th>
              <th className="py-1 pr-2 font-semibold">Seed</th>
              {multiConfig && <th className="py-1 pr-2 font-semibold">Cfg</th>}
              <th className="py-1 pr-2 text-right font-semibold">Spins</th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="Return to player — simulated payout percentage"
              >
                RTP %
              </th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="RTP change vs previous run of the same config"
              >
                Δ
              </th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="Hit frequency — % of spins with any win"
              >
                Hit %
              </th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="Hit-frequency change vs previous run of the same config"
              >
                Δ
              </th>
              {/* normal-case: row-level `uppercase` would render σ as Σ */}
              <th
                className="py-1 pr-2 text-right font-semibold normal-case"
                title="Volatility — standard deviation of per-spin payouts"
              >
                σ
              </th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="Volatility change vs previous run of the same config"
              >
                Δ
              </th>
              <th
                className="py-1 pr-2 text-right font-semibold"
                title="Largest single-spin win observed (stake multiples)"
              >
                Max win
              </th>
              <th
                className="py-1 text-right font-semibold"
                title="Wall-clock run time"
              >
                ms
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-paper-border tabular-nums dark:divide-term-border">
            {entries.map(({ record: r, deltas }) => (
              <tr key={r.id}>
                <td className="py-1 pr-2 text-paper-text dark:text-term-text">
                  {new Date(r.completedAt).toLocaleTimeString()}
                </td>
                <td className="py-1 pr-2 font-mono text-paper-text dark:text-term-text">
                  {r.seed.slice(0, 8)}…
                </td>
                {multiConfig && (
                  <td className="py-1 pr-2 font-mono text-paper-label dark:text-term-label">
                    {r.configHash.slice(0, 5)}
                  </td>
                )}
                <td className="py-1 pr-2 text-right text-paper-text dark:text-term-text">
                  {r.nSpins.toLocaleString()}
                </td>
                <td className="py-1 pr-2 text-right font-semibold text-paper-bright dark:text-term-bright">
                  {r.rtpPct.toFixed(3)}
                </td>
                <DeltaCell value={deltas?.rtpPct} decimals={3} />
                <td className="py-1 pr-2 text-right text-paper-text dark:text-term-text">
                  {r.hitPct.toFixed(2)}
                </td>
                <DeltaCell value={deltas?.hitPct} decimals={2} />
                <td className="py-1 pr-2 text-right text-paper-text dark:text-term-text">
                  {r.stdev.toFixed(2)}
                </td>
                <DeltaCell value={deltas?.stdev} decimals={2} />
                <td className="py-1 pr-2 text-right text-paper-text dark:text-term-text">
                  {r.maxWin.toFixed(2)}
                </td>
                <td className="py-1 text-right text-paper-hint dark:text-term-hint">
                  {Math.round(r.elapsedMs).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 border-t border-paper-border pt-2 text-[10px] text-paper-label dark:border-term-border dark:text-term-label">
        <span className="normal-case">
          Δ = change vs previous run of same config (
          <span className="text-emerald-700 dark:text-emerald-500">▲</span> up
          · <span className="text-rose-700 dark:text-rose-400">▼</span> down ·
          “·” ≈ no change · blank = first run of config) · σ = payout stdev
          (volatility)
        </span>
      </p>
    </section>
  );
}

/** Signed delta cell: ▲ emerald / ▼ rose / · dim when ≈0 / empty when null. */
function DeltaCell({
  value,
  decimals,
}: {
  value: number | undefined;
  decimals: number;
}) {
  if (value == null) {
    return (
      <td className="py-1 pr-2 text-right text-paper-hint dark:text-term-hint" />
    );
  }
  const eps = 10 ** -decimals / 2;
  if (Math.abs(value) < eps) {
    return (
      <td className="py-1 pr-2 text-right text-paper-hint dark:text-term-hint">
        ·
      </td>
    );
  }
  const up = value > 0;
  return (
    <td
      className={`py-1 pr-2 text-right ${
        up
          ? "text-emerald-700 dark:text-emerald-500"
          : "text-rose-700 dark:text-rose-400"
      }`}
    >
      {up ? "▲" : "▼"}
      {Math.abs(value).toFixed(decimals)}
    </td>
  );
}
